import { ReactNode, useState, useEffect, useCallback, useMemo } from 'react';
import { getJWTTokens, removeJWTTokens, setJWTokens } from '@shared/libs/local-storage';
import { AuthContext, AuthContextData } from '@shared/libs/context/AuthContext';
import { isTokenExpired } from '@shared/libs/jwt';
import { logoutRequest } from '@features/auth/api/logout';
import { clearFilters } from '@shared/api/filters-ws';

interface IProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: IProviderProps) => {
  const [authState, setAuthState] = useState<AuthContextData>(getJWTTokens());

  useEffect(() => {
    const handleStorageChange = () => {
      setAuthState(getJWTTokens());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = useCallback((email: string, accessToken: string, refreshToken: string, groupName: string) => {
    setAuthState({ email, accessToken, refreshToken, groupName });
    setJWTokens(email, accessToken, refreshToken, groupName);
  }, []);

  const logout = useCallback(async () => {
    const { refreshToken } = getJWTTokens();

    clearFilters();

    removeJWTTokens();
    setAuthState({
      email: null,
      accessToken: null,
      refreshToken: null,
      groupName: null,
    });

    if (refreshToken) {
      await logoutRequest(refreshToken);
    }
  }, []);

  const checkAuth = useCallback(() => {
    const { accessToken } = getJWTTokens();
    return !!accessToken && !isTokenExpired(accessToken);
  }, []);

  const contextValue = useMemo(
    () => ({ data: authState, logout, login, checkAuth }),
    [authState, logout, login, checkAuth]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
